/**
 * Therapy & Background Section
 *
 * Allows users to configure their default therapy style, relationship context,
 * and custom therapist instructions for personalized AI feedback.
 */

import { useState, useEffect } from "react";
import {
  useCurrentUser,
  useUpdateCurrentUser,
} from "../../hooks/useCurrentUser";
import { useValidateText } from "../../hooks/useSpeakerMemories";
import { TherapyStyleSelector } from "../TherapyStyleSelector";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Card } from "../ui/card";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import type { TextValidationResult } from "../../services/speakerMemories";

export function TherapyBackgroundSection() {
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useCurrentUser();
  const updateUser = useUpdateCurrentUser();

  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const [relationshipContext, setRelationshipContext] = useState("");
  const [customTherapistPrompt, setCustomTherapistPrompt] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [biasWarning, setBiasWarning] = useState<TextValidationResult | null>(
    null
  );
  const validateText = useValidateText();

  useEffect(() => {
    if (user) {
      if (user.default_therapy_style_id) {
        setSelectedStyleId(user.default_therapy_style_id);
      }
      setRelationshipContext(user.relationship_context || "");
      setCustomTherapistPrompt(user.custom_therapist_prompt || "");
    }
  }, [user]);

  const hasChanges =
    selectedStyleId !== (user?.default_therapy_style_id || null) ||
    relationshipContext !== (user?.relationship_context || "") ||
    customTherapistPrompt !== (user?.custom_therapist_prompt || "");

  const handleSave = async () => {
    try {
      // Run bias check on relationship context if it changed
      const contextChanged =
        relationshipContext !== (user?.relationship_context || "");
      if (contextChanged && relationshipContext.trim()) {
        try {
          const result = await validateText.mutateAsync({
            text: relationshipContext,
            context: "relationship_description",
          });
          if (result.verdict === "FLAGGED") {
            setBiasWarning(result);
            // Don't block — still save below, just show warning
          } else {
            setBiasWarning(null);
          }
        } catch {
          // On validation infra failure, proceed without blocking
        }
      } else if (!contextChanged) {
        setBiasWarning(null);
      }

      await updateUser.mutateAsync({
        default_therapy_style_id: selectedStyleId,
        relationship_context: relationshipContext,
        custom_therapist_prompt: customTherapistPrompt,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // Error is handled by mutation state
    }
  };

  if (userLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="mb-4 h-6 w-48 rounded bg-muted" />
          <div className="h-4 w-64 rounded bg-muted" />
        </div>
      </Card>
    );
  }

  if (userError) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <p className="text-destructive">
          Error loading settings: {userError.message}
        </p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            Default Therapy Style
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose your preferred therapeutic framework for AI-generated
            feedback on your reflections. This will be the default style used
            unless you select a different one when creating a reflection.
          </p>
        </div>

        <TherapyStyleSelector
          value={selectedStyleId}
          onChange={setSelectedStyleId}
          variant="cards"
          disabled={updateUser.isPending}
        />

        {user.default_therapy_style && (
          <p className="mt-4 text-sm text-muted-foreground">
            Current default: <strong>{user.default_therapy_style.name}</strong>
          </p>
        )}
      </Card>

      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            Relationship Context
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Provide background information about your relationship that will
            help the AI provide more personalized feedback. This context will be
            included when generating insights and feedback.
          </p>
        </div>

        <Textarea
          value={relationshipContext}
          onChange={(e) => setRelationshipContext(e.target.value)}
          placeholder="Example: We've been together for 5 years, living together for 2 years. We have different communication styles - I'm more direct while my partner prefers to process emotions before discussing them."
          className="min-h-[120px]"
          disabled={updateUser.isPending}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          This information is private and only used to improve AI feedback
          quality.
        </p>

        {biasWarning && biasWarning.verdict === "FLAGGED" && (
          <div className="mt-3 rounded-lg border border-amber-500/50 bg-amber-50 p-4 dark:bg-amber-950/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Some content may affect therapist objectivity
                </p>
                <ul className="list-disc pl-4 text-sm text-amber-700 dark:text-amber-300">
                  {biasWarning.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
                {biasWarning.suggestion && (
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Suggestion:</strong> {biasWarning.suggestion}
                  </p>
                )}
                <button
                  onClick={() => setBiasWarning(null)}
                  className="text-xs text-amber-600 dark:text-amber-400 underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            Custom Therapist Instructions
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Customize how the AI therapist responds to you. You can specify
            tone, focus areas, or specific approaches you'd like the AI to take
            when providing feedback.
          </p>
        </div>

        <Textarea
          value={customTherapistPrompt}
          onChange={(e) => setCustomTherapistPrompt(e.target.value)}
          placeholder="Example: Please be gentle and encouraging in your feedback. Focus on helping me understand my partner's perspective. Avoid being too direct or critical."
          className="min-h-[120px]"
          disabled={updateUser.isPending}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          Examples: "Be direct and solution-focused", "Help me see both
          perspectives", "Focus on emotional validation"
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateUser.isPending}
          >
            {updateUser.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {saveSuccess ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
          {saveSuccess && (
            <span className="text-sm text-green-600 dark:text-green-400">
              Your preferences have been updated.
            </span>
          )}
          {updateUser.error && (
            <span className="text-sm text-destructive">
              Failed to save. Please try again.
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
